import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import workbenchLogo from '../assets/workbench_logo.png';
import { COMPANY_INFO } from './quotation-defaults';
import { QUOTATION_UNITS } from './enums';

const formatDate = (value) => {
    if (!value) return '';
    return moment(value).format('DD MMM YYYY');
};

const formatAmount = (amount) => {
    const safe = Number(amount || 0);
    return safe.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const loadImageAsDataUrl = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
        try {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve({ dataUrl: canvas.toDataURL('image/png'), width: img.naturalWidth, height: img.naturalHeight });
        } catch (err) {
            reject(err);
        }
    };
    img.onerror = reject;
    img.src = src;
});

const computeDiscountAmount = (subtotal, discount = {}) => {
    if (!discount || !discount.type) return 0;
    const value = Number(discount.value || 0);
    if (discount.type === 'percent') return (subtotal * value) / 100;
    if (discount.type === 'flat') return value;
    return 0;
};

const buildItemLabel = (item) => {
    const base = item.unit === QUOTATION_UNITS.OFFICE ? 'Office' : 'Seat';
    const seats = item.unit === QUOTATION_UNITS.OFFICE && Number(item.seatsPerOffice) > 0
        ? ` (${item.seatsPerOffice} seats)`
        : '';
    const area = item.area ? ` — ${item.area}` : '';
    return `${base}${seats}${area}`;
};

// Render a labelled multi-line text section. Returns the new y position.
const renderTextBlock = (doc, { title, body, y, left, contentWidth, pageHeight, bottomMargin = 60 }) => {
    if (!body) return y;

    const titleHeight = 18;
    const lineHeight = 12;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);

    const wrappedLines = doc.splitTextToSize(String(body), contentWidth);
    const blockHeight = titleHeight + wrappedLines.length * lineHeight + 6;

    if (y + blockHeight > pageHeight - bottomMargin) {
        doc.addPage();
        // eslint-disable-next-line no-param-reassign
        y = 50;
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    doc.text(title, left, y);
    // eslint-disable-next-line no-param-reassign
    y += titleHeight;

    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    wrappedLines.forEach((line) => {
        if (y > pageHeight - bottomMargin) {
            doc.addPage();
            // eslint-disable-next-line no-param-reassign
            y = 50;
        }
        doc.text(line, left, y);
        // eslint-disable-next-line no-param-reassign
        y += lineHeight;
    });

    return y + 10;
};

export const downloadQuotationPdf = async ({ quotation } = {}) => {
    if (!quotation) return false;

    const currency = quotation?.pricing?.currency || 'PKR';
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const left = 40;
    const right = 40;
    const contentWidth = pageWidth - left - right;

    // === Header: Logo (left) + "Quotation" title (right) ===
    let logoBottom = 40;
    try {
        const { dataUrl, width, height } = await loadImageAsDataUrl(workbenchLogo);
        const targetHeight = 50;
        const ratio = width / height;
        const targetWidth = targetHeight * ratio;
        doc.addImage(dataUrl, 'PNG', left, 40, targetWidth, targetHeight);
        logoBottom = 40 + targetHeight;
    } catch (err) {
        // continue without logo
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(28);
    doc.setTextColor(40, 40, 40);
    doc.text('Quotation', pageWidth - right, 65, { align: 'right' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    doc.text(`# ${quotation.quotationNumber || ''}`, pageWidth - right, 85, { align: 'right' });

    // === Company info block (under logo, left) ===
    let y = logoBottom + 18;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    doc.text(COMPANY_INFO.name, left, y);
    y += 14;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    COMPANY_INFO.addressLines.forEach((line) => {
        doc.text(line, left, y);
        y += 12;
    });

    y += 14;

    // === TO / Quotation Date row ===
    const toBlockTopY = y;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text('TO', left, toBlockTopY);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    const customer = quotation.customer || {};
    const customerName = customer.company || customer.fullName || '';
    doc.text(customerName, left, toBlockTopY + 16);

    let belowCustomer = toBlockTopY + 16;
    const subLines = [];
    if (customer.fullName && customer.company && customer.fullName !== customer.company) {
        subLines.push(customer.fullName);
    }
    if (customer.email) subLines.push(customer.email);
    if (customer.phone) subLines.push(customer.phone);
    if (customer.address) subLines.push(customer.address);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(110, 110, 110);
    subLines.forEach((line) => {
        belowCustomer += 12;
        doc.text(String(line), left, belowCustomer);
    });

    // Right side: Quotation Date / Valid Until — render each as a single right-aligned line
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(`Quotation Date :  ${formatDate(quotation.issueDate)}`, pageWidth - right, toBlockTopY + 16, { align: 'right' });

    if (quotation.validUntil) {
        doc.text(`Valid Until :  ${formatDate(quotation.validUntil)}`, pageWidth - right, toBlockTopY + 32, { align: 'right' });
    }

    y = Math.max(belowCustomer, toBlockTopY + 32) + 24;

    // === Line items table ===
    const lineItems = Array.isArray(quotation.lineItems) ? quotation.lineItems : [];
    const itemRows = lineItems.map((item, idx) => {
        const qty = Number(item.quantity || 0);
        const unitPrice = Number(item.unitPrice || 0);
        const seats = Number(item.seatsPerOffice || 0);
        const fallbackTotal = item.unit === QUOTATION_UNITS.OFFICE
            ? qty * seats * unitPrice
            : qty * unitPrice;
        const lineTotal = Number(item.lineTotal ?? fallbackTotal);
        return [
            String(idx + 1),
            buildItemLabel(item),
            qty.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            formatAmount(unitPrice),
            formatAmount(lineTotal),
        ];
    });

    autoTable(doc, {
        startY: y,
        margin: { left, right },
        head: [['#', 'Item & Description', 'Qty', 'Rate', 'Amount']],
        body: itemRows.length > 0 ? itemRows : [['', '—', '', '', '']],
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: 8, lineColor: [220, 220, 220], lineWidth: 0.5 },
        headStyles: {
            fillColor: [245, 245, 245],
            textColor: [80, 80, 80],
            fontStyle: 'normal',
            lineColor: [220, 220, 220],
            lineWidth: 0.5,
        },
        bodyStyles: { textColor: [60, 60, 60] },
        columnStyles: {
            0: { cellWidth: 24, halign: 'left' },
            1: { cellWidth: 'auto', halign: 'left' },
            2: { cellWidth: 60, halign: 'right' },
            3: { cellWidth: 80, halign: 'right' },
            4: { cellWidth: 90, halign: 'right' },
        },
        didDrawPage: () => {
            // No-op; footer added in final loop.
        },
    });

    y = doc.lastAutoTable.finalY;

    // === Pricing summary (right-aligned, gray bar style) ===
    const pricing = quotation.pricing || {};
    const subtotal = Number(pricing.subtotal || 0);
    const discountAmount = computeDiscountAmount(subtotal, pricing.discount);
    const afterDiscount = Math.max(0, subtotal - discountAmount);
    const taxPercent = Number(pricing.taxPercent || 0);
    const taxAmount = Number(pricing.taxAmount ?? (afterDiscount * taxPercent / 100));
    const securityDeposit = Number(pricing.securityDeposit || 0);
    const advance = Number(pricing.advance || 0);
    const total = Number(pricing.total || 0);

    const summaryRows = [['Sub Total', formatAmount(subtotal)]];
    if (discountAmount > 0) {
        const discountLabel = pricing.discount?.type === 'percent'
            ? `Discount (${Number(pricing.discount.value || 0)}%)`
            : 'Discount';
        summaryRows.push([discountLabel, `- ${formatAmount(discountAmount)}`]);
    }
    if (taxAmount > 0) {
        summaryRows.push([`Tax (${taxPercent}%)`, formatAmount(taxAmount)]);
    }
    if (securityDeposit > 0) summaryRows.push(['Security Deposit', formatAmount(securityDeposit)]);
    if (advance > 0) summaryRows.push(['Advance', `- ${formatAmount(advance)}`]);
    summaryRows.push([`Total`, `${currency}${formatAmount(total)}`]);

    autoTable(doc, {
        startY: y,
        margin: { left: pageWidth - right - 260, right },
        body: summaryRows,
        theme: 'plain',
        styles: { fontSize: 10, cellPadding: 6 },
        columnStyles: {
            0: { cellWidth: 160, halign: 'right', textColor: [80, 80, 80] },
            1: { cellWidth: 100, halign: 'right', textColor: [60, 60, 60] },
        },
        didParseCell: (data) => {
            const isLast = data.row.index === summaryRows.length - 1;
            if (isLast) {
                data.cell.styles.fillColor = [245, 245, 245];
                data.cell.styles.fontStyle = 'bold';
                data.cell.styles.textColor = [40, 40, 40];
            } else if (data.row.index === 0) {
                data.cell.styles.fillColor = [250, 250, 250];
            }
        },
    });

    y = doc.lastAutoTable.finalY + 6;

    if (pricing.discount?.reason) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(`Discount reason: ${pricing.discount.reason}`, left, y + 8);
        y += 16;
    }

    y += 24;

    // === Notes / Terms / Inclusions / Exclusions ===
    const terms = quotation.terms || {};
    y = renderTextBlock(doc, { title: 'Notes', body: terms.notes, y, left, contentWidth, pageHeight });
    y = renderTextBlock(doc, { title: 'Terms & Conditions', body: terms.termsAndConditions, y, left, contentWidth, pageHeight });
    y = renderTextBlock(doc, { title: 'Inclusions', body: terms.inclusions, y, left, contentWidth, pageHeight });
    y = renderTextBlock(doc, { title: 'Exclusions', body: terms.exclusions, y, left, contentWidth, pageHeight });

    y += 10;
    if (y > pageHeight - 60) {
        doc.addPage();
        y = 50;
    }
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text('We look forward to the opportunity to serve you and build a successful business relationship.', left, y);

    // === Footer page numbers ===
    const totalPages = doc.getNumberOfPages();
    for (let p = 1; p <= totalPages; p += 1) {
        doc.setPage(p);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`${p}`, pageWidth - right, pageHeight - 24, { align: 'right' });
    }

    const fileName = `${quotation.quotationNumber || 'quotation'}.pdf`;
    doc.save(fileName);
    return true;
};
